// @ts-nocheck
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { api } from "@/api";
import { AGENTS, DEFAULT_ENABLED_AGENTS, isAgentEnabled as checkAgentEnabled } from "./agentRegistry";
import { buildOliSystemPrompt, DEFAULT_FIRM_CONFIG, GOVERNANCE_RULES, OLI_FOUNDATION_PROMPT } from "./governanceRules";

export interface OliConfig {
  // Prompt
  mergedSystemPrompt: string;
  firmConfig: typeof DEFAULT_FIRM_CONFIG;
  trainingData: any[];

  // Model
  model: string;
  temperature: number;
  maxTokens: number;
  useClientData: boolean;
  formality: string;

  // Agent control
  enabledAgentIds: string[];
  confidenceThreshold: number;
  explainabilityEnabled: boolean;
  dataFreshnessMode: string;
  firmTerminology: string;

  // Governance
  governanceRules: string[];

  // Helpers
  isAgentEnabled: (id: string) => boolean;

  // Status
  isLoading: boolean;
  error: Error | null;
}

const RESERVED_CATEGORIES = new Set([
  "oli_model",
  "prompt_template",
  "workflow_automation",
  "custom_tool",
  "feedback",
  "version_snapshot",
  "conversation_log",
  "agent_test_scenario",
  "agent_health_event",
]);

export function useOliConfig(userRole?: string): OliConfig {
  const {
    data: firmConfigRecord,
    isLoading: loadingFirmConfig,
    error: firmConfigError,
  } = useQuery({
    queryKey: ["oli-config"],
    queryFn: async () => {
      try {
        if (api.entities.GlobalSettings.filter) {
          const records = await api.entities.GlobalSettings.filter({ setting_key: "oli_firm_config" });
          return records?.[0] || null;
        }
        const records = await api.entities.GlobalSettings.list();
        return (records || []).find(
          (r: any) => r.setting_key === "oli_firm_config" || r.key === "oli_firm_config"
        ) || null;
      } catch {
        return null;
      }
    },
    staleTime: 300_000,
  });

  const {
    data: globalSettings,
    isLoading: loadingSettings,
  } = useQuery({
    queryKey: ["global-settings"],
    queryFn: async () => {
      try {
        return (await api.entities.GlobalSettings.list())?.[0] || null;
      } catch {
        return null;
      }
    },
    staleTime: 300_000,
  });

  const {
    data: allOliRecords = [],
    isLoading: loadingTraining,
  } = useQuery({
    queryKey: ["oli-training"],
    queryFn: async () => {
      try {
        return await api.entities.OliPersonalization.list("-created_at", 500);
      } catch {
        return [];
      }
    },
    staleTime: 300_000,
  });

  const firmConfig = useMemo(() => {
    const raw = firmConfigRecord?.setting_value || firmConfigRecord?.value || firmConfigRecord?.data?.setting_value;
    return raw || DEFAULT_FIRM_CONFIG;
  }, [firmConfigRecord]);

  const trainingData = useMemo(() => {
    const filtered = (allOliRecords as any[]).filter(
      (r) => !RESERVED_CATEGORIES.has(r.category || r.data?.category)
    );
    // Manual entries first, auto-learned second (so manual training always gets into the 30-item prompt slice)
    const manual = filtered.filter((r) => !r.data?.auto_logged);
    const autoLearned = filtered.filter((r) => r.data?.auto_logged);
    return [...manual, ...autoLearned];
  }, [allOliRecords]);

  const enabledAgentIds: string[] = globalSettings?.oli_enabled_agents || DEFAULT_ENABLED_AGENTS;
  const confidenceThreshold: number = globalSettings?.oli_agent_confidence_threshold ?? 0.75;
  const dataFreshnessMode: string = globalSettings?.oli_agent_data_freshness_mode || "live_when_available";
  const firmTerminology: string = globalSettings?.oli_firm_terminology || "";
  const explainabilityEnabled: boolean = globalSettings?.oli_explainability_enabled !== false;
  const model: string = globalSettings?.oli_model || "claude_haiku_4_5";
  const temperature: number = globalSettings?.oli_creativity ?? 0.2;
  const maxTokens: number = globalSettings?.oli_max_tokens ?? 1200;
  const useClientData: boolean = globalSettings?.oli_use_client_data !== false;
  const formality: string = globalSettings?.oli_formality || "balanced";

  const mergedSystemPrompt = useMemo(
    () =>
      buildOliSystemPrompt(firmConfig, trainingData, {
        enabled_agents: enabledAgentIds,
        user_role: userRole || "advisor",
        confidence_threshold: confidenceThreshold,
        data_freshness_mode: dataFreshnessMode,
        firm_terminology: firmTerminology,
        explainability_enabled: explainabilityEnabled,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [firmConfig, trainingData, enabledAgentIds, userRole, confidenceThreshold, dataFreshnessMode, firmTerminology, explainabilityEnabled]
  );

  const isAgentEnabled = (id: string) => checkAgentEnabled(id, enabledAgentIds);

  return {
    mergedSystemPrompt,
    firmConfig,
    trainingData,
    model,
    temperature,
    maxTokens,
    useClientData,
    formality,
    enabledAgentIds,
    confidenceThreshold,
    explainabilityEnabled,
    dataFreshnessMode,
    firmTerminology,
    governanceRules: GOVERNANCE_RULES,
    isAgentEnabled,
    isLoading: loadingFirmConfig || loadingSettings || loadingTraining,
    error: (firmConfigError as Error | null) || null,
  };
}
